import { useState, useEffect, useRef } from "react";
import { Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { NativeSelect } from "@/components/ui/native-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ArrowLeft, Camera, Upload, Cloud, Palette, Wrench, CheckCircle, Clock, Image } from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

const weatherOptions = [
  { value: "sunny", label: "Ensolarado" },
  { value: "cloudy", label: "Nublado" },
  { value: "rainy", label: "Chuvoso" },
  { value: "stormy", label: "Tempestuoso" }
];

function DailyLogsList() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/daily-logs`);
      setLogs(response.data);
    } catch (error) {
      toast.error("Erro ao carregar registros");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (logId) => {
    try {
      await axios.put(`${API}/daily-logs/${logId}/approve`);
      toast.success("Registro aprovado!");
      fetchLogs();
    } catch (error) {
      toast.error("Erro ao aprovar registro");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="daily-logs-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Diário de Obra (RDO)</h1>
        <Link to="/daily-logs/new">
          <Button className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white" data-testid="new-log-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo Registro
          </Button>
        </Link>
      </div>

      {logs.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nenhum registro encontrado</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {logs.map((log) => (
            <Card key={log.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={log.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                        {log.status === "approved" ? "Aprovado" : "Pendente"}
                      </Badge>
                      {log.weather && (
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Cloud className="h-3 w-3" />
                          {weatherOptions.find(w => w.value === log.weather)?.label || log.weather}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 line-clamp-2">{log.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span>{log.created_by_name || "Sistema"}</span>
                      <span>{new Date(log.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      <span>{log.workers_present} trabalhadores</span>
                      {log.photos?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Image className="h-3 w-3" />
                          {log.photos.length} fotos
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {user?.role !== "operational" && log.status !== "approved" && (
                      <Button
                        size="sm"
                        onClick={() => handleApprove(log.id)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`approve-btn-${log.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Aprovar
                      </Button>
                    )}
                    <Link to={`/daily-logs/${log.id}`}>
                      <Button variant="outline" size="sm">Ver</Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function NewDailyLog() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [createdLogId, setCreatedLogId] = useState(null);
  const [photos, setPhotos] = useState([]);
  
  const [formData, setFormData] = useState({
    project_id: "",
    service_order_id: "",
    description: "",
    weather: "",
    workers_present: 0,
    technical_memory: {
      materials: [],
      paint_codes: [],
      equipment: []
    }
  });

  const [newMaterial, setNewMaterial] = useState({ brand: "", name: "" });
  const [newPaintCode, setNewPaintCode] = useState({ brand: "", code: "", color: "" });
  const [newEquipment, setNewEquipment] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/service-orders`)
      ]);
      setProjects(projectsRes.data);
      setServiceOrders(ordersRes.data);
    } catch (error) {
      console.log("Error fetching data:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/daily-logs`, formData);
      setCreatedLogId(response.data.id);
      toast.success("Registro criado! Agora você pode adicionar fotos.");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao criar registro"));
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    if (!createdLogId) return;
    
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingPhoto(true);
    
    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        formDataUpload.append("description", "");

        await axios.post(`${API}/daily-logs/${createdLogId}/photos`, formDataUpload, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          setPhotos(prev => [...prev, { name: file.name, preview: e.target.result }]);
        };
        reader.readAsDataURL(file);
        
        toast.success(`Foto ${file.name} enviada!`);
      } catch (error) {
        toast.error(`Erro ao enviar ${file.name}`);
      }
    }
    
    setUploadingPhoto(false);
  };

  const addMaterial = () => {
    if (newMaterial.brand && newMaterial.name) {
      setFormData({
        ...formData,
        technical_memory: {
          ...formData.technical_memory,
          materials: [...formData.technical_memory.materials, newMaterial]
        }
      });
      setNewMaterial({ brand: "", name: "" });
    }
  };

  const addPaintCode = () => {
    if (newPaintCode.brand && newPaintCode.code) {
      setFormData({
        ...formData,
        technical_memory: {
          ...formData.technical_memory,
          paint_codes: [...formData.technical_memory.paint_codes, newPaintCode]
        }
      });
      setNewPaintCode({ brand: "", code: "", color: "" });
    }
  };

  const addEquipment = () => {
    if (newEquipment) {
      setFormData({
        ...formData,
        technical_memory: {
          ...formData.technical_memory,
          equipment: [...formData.technical_memory.equipment, newEquipment]
        }
      });
      setNewEquipment("");
    }
  };

  const handleFinish = () => {
    toast.success("Registro salvo com sucesso!");
    navigate("/daily-logs");
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="new-daily-log">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/daily-logs">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Novo Registro Diário</h1>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Projeto *</Label>
                <NativeSelect
                  value={formData.project_id}
                  onValueChange={(value) => setFormData({...formData, project_id: value})}
                  disabled={!!createdLogId}
                  placeholder="Selecione o projeto"
                  options={projects.map(p => ({ value: p.id, label: p.name }))}
                  data-testid="select-project"
                />
              </div>
              <div className="space-y-2">
                <Label>Ordem de Serviço</Label>
                <NativeSelect
                  value={formData.service_order_id}
                  onValueChange={(value) => setFormData({...formData, service_order_id: value})}
                  disabled={!!createdLogId}
                  placeholder="Selecione (opcional)"
                  options={serviceOrders.map(so => ({ value: so.id, label: so.title }))}
                  data-testid="select-service-order"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Clima</Label>
                <NativeSelect
                  value={formData.weather}
                  onValueChange={(value) => setFormData({...formData, weather: value})}
                  disabled={!!createdLogId}
                  placeholder="Selecione"
                  options={weatherOptions.map(w => ({ value: w.value, label: w.label }))}
                  data-testid="select-weather"
                />
              </div>
              <div className="space-y-2">
                <Label>Trabalhadores Presentes</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.workers_present}
                  onChange={(e) => setFormData({...formData, workers_present: parseInt(e.target.value) || 0})}
                  disabled={!!createdLogId}
                  data-testid="input-workers"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descrição das Atividades *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descreva as atividades realizadas no dia..."
                rows={4}
                required
                disabled={!!createdLogId}
                data-testid="input-description"
              />
            </div>

            {/* Technical Memory */}
            <Tabs defaultValue="materials" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="materials">Materiais</TabsTrigger>
                <TabsTrigger value="paint">Tintas</TabsTrigger>
                <TabsTrigger value="equipment">Equipamentos</TabsTrigger>
              </TabsList>
              
              <TabsContent value="materials" className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Marca"
                    value={newMaterial.brand}
                    onChange={(e) => setNewMaterial({...newMaterial, brand: e.target.value})}
                    disabled={!!createdLogId}
                  />
                  <Input
                    placeholder="Nome do material"
                    value={newMaterial.name}
                    onChange={(e) => setNewMaterial({...newMaterial, name: e.target.value})}
                    disabled={!!createdLogId}
                  />
                  <Button type="button" onClick={addMaterial} variant="outline" disabled={!!createdLogId}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.technical_memory.materials.map((m, i) => (
                    <Badge key={i} variant="secondary" className="flex items-center gap-1">
                      <Wrench className="h-3 w-3" />
                      {m.brand} - {m.name}
                    </Badge>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="paint" className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Marca"
                    value={newPaintCode.brand}
                    onChange={(e) => setNewPaintCode({...newPaintCode, brand: e.target.value})}
                    disabled={!!createdLogId}
                  />
                  <Input
                    placeholder="Código"
                    value={newPaintCode.code}
                    onChange={(e) => setNewPaintCode({...newPaintCode, code: e.target.value})}
                    disabled={!!createdLogId}
                  />
                  <Input
                    placeholder="Cor"
                    value={newPaintCode.color}
                    onChange={(e) => setNewPaintCode({...newPaintCode, color: e.target.value})}
                    disabled={!!createdLogId}
                  />
                  <Button type="button" onClick={addPaintCode} variant="outline" disabled={!!createdLogId}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.technical_memory.paint_codes.map((p, i) => (
                    <Badge key={i} variant="secondary" className="flex items-center gap-1">
                      <Palette className="h-3 w-3" />
                      {p.brand} - {p.code} ({p.color})
                    </Badge>
                  ))}
                </div>
              </TabsContent>
              
              <TabsContent value="equipment" className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Nome do equipamento"
                    value={newEquipment}
                    onChange={(e) => setNewEquipment(e.target.value)}
                    disabled={!!createdLogId}
                  />
                  <Button type="button" onClick={addEquipment} variant="outline" disabled={!!createdLogId}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.technical_memory.equipment.map((e, i) => (
                    <Badge key={i} variant="secondary">
                      <Wrench className="h-3 w-3 mr-1" />
                      {e}
                    </Badge>
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            {!createdLogId && (
              <Button
                type="submit"
                className="w-full bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white h-11"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? "Criando..." : "Criar Registro"}
              </Button>
            )}
          </form>

          {/* Photo Upload Section */}
          {createdLogId && (
            <div className="mt-6 pt-6 border-t space-y-4">
              <Label className="text-lg font-semibold text-luvcorp-navy flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Fotos do Registro
              </Label>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
              />
              
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-luvcorp-gold transition-colors"
              >
                <Upload className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600">
                  {uploadingPhoto ? "Enviando..." : "Clique para enviar fotos"}
                </p>
              </div>

              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-4">
                  {photos.map((photo, i) => (
                    <div key={i} className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                      <img src={photo.preview} alt={photo.name} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}

              <Button
                onClick={handleFinish}
                className="w-full bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white h-11"
                data-testid="finish-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Finalizar Registro
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function DailyLogsPage() {
  return (
    <MainLayout>
      <Routes>
        <Route index element={<DailyLogsList />} />
        <Route path="new" element={<NewDailyLog />} />
      </Routes>
    </MainLayout>
  );
}
